import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChartBarSquareIcon,
  PlayIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ScaleIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { fairLendingApi } from '../lib/api';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTeamId } from '../hooks/useTeamId';
import { PageHeader, LoadingSpinner } from '../components/ui';
import toast from 'react-hot-toast';

type TabId = 'overview' | 'findings' | 'matched-pairs' | 'trends';

export default function RegressionAnalysisPage() {
  const queryClient = useQueryClient();
  const { hasFeature, isLoading: featuresLoading } = useFeatureAccess();
  const { teamId } = useTeamId();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['regression-analyses', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.listRegressions(teamId!, { limit: 50 });
      return res.data;
    },
    enabled: !!teamId,
    // Analyses now run on a background worker (P1.9). Poll while any is QUEUED/RUNNING, stop once
    // everything has settled — same pattern as CRA lending / website scans.
    refetchInterval: (query) => {
      const rows = (query.state.data as any)?.analyses as Array<{ status: string }> | undefined;
      const pending = rows?.some((a) => a.status === 'QUEUED' || a.status === 'RUNNING');
      return pending ? 5000 : false;
    },
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['regression-detail', teamId, selectedAnalysisId],
    queryFn: async () => {
      if (!selectedAnalysisId) return null;
      const res = await fairLendingApi.getRegression(teamId!, selectedAnalysisId);
      return res.data;
    },
    enabled: !!selectedAnalysisId && !!teamId,
    // Keep the open detail view live while its analysis is still running on the worker.
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.status;
      return status === 'QUEUED' || status === 'RUNNING' ? 5000 : false;
    },
  });

  const { data: trendsData } = useQuery({
    queryKey: ['regression-trends', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.getRegressionTrends(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const { data: engineStatus } = useQuery({
    queryKey: ['regression-engine-status', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.getRegressionEngineStatus(teamId!);
      return res.data;
    },
    enabled: !!teamId,
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  const runMutation = useMutation({
    mutationFn: (data: { validationId: string }) =>
      fairLendingApi.runFullRegression(teamId!, data),
    onSuccess: () => {
      // Analyses are queued and processed on a background worker; the list polls until they finish.
      toast.success('Regression analysis queued — results will appear as each completes');
      queryClient.invalidateQueries({ queryKey: ['regression-analyses'] });
    },
    onError: () => {
      toast.error('Failed to queue regression analysis');
    },
  });

  if (featuresLoading) return <LoadingSpinner className="py-16" />;
  if (!hasFeature('module_fair_lending')) {
    return (
      <div className="text-center py-16">
        <ChartBarSquareIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Regression Analysis</h2>
        <p className="mt-2 text-gray-600 max-w-md mx-auto">
          IFLEP-aligned regression analysis is available on the Enterprise plan.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  const analyses = listData?.analyses || [];
  const trends = trendsData?.trends || [];

  // Summary stats
  const completedAnalyses = analyses.filter((a: any) => a.status === 'COMPLETED');
  const highRiskCount = completedAnalyses.reduce(
    (sum: number, a: any) => sum + (a.findingsCount || 0), 0
  );
  const latestAnalysis = completedAnalyses[0];

  const tabs: { id: TabId; name: string; icon: any }[] = [
    { id: 'overview', name: 'Overview', icon: ChartBarSquareIcon },
    { id: 'findings', name: 'Findings', icon: ScaleIcon },
    { id: 'matched-pairs', name: 'Matched Pairs', icon: UserGroupIcon },
    { id: 'trends', name: 'Trends', icon: ArrowTrendingUpIcon },
  ];

  const engineUnavailable = engineStatus && !engineStatus.available;

  return (
    <div>
      <PageHeader
        title="Regression Analysis"
        description="IFLEP-aligned statistical regression for denial, pricing, and steering disparities"
        actions={
          <button
            onClick={() => {
              const validationId = prompt('Enter HMDA Validation ID:');
              if (validationId) {
                runMutation.mutate({ validationId });
              }
            }}
            disabled={runMutation.isPending || engineUnavailable}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={engineUnavailable ? 'Regression engine is currently unavailable' : ''}
          >
            <PlayIcon className="h-4 w-4" />
            {runMutation.isPending ? 'Running...' : 'Run Full Analysis'}
          </button>
        }
      />

      {/* Engine Unavailable Banner */}
      {engineUnavailable && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-800">Regression Engine Unavailable</h3>
              <p className="mt-1 text-sm text-amber-700">
                The Python statistical regression engine is not currently reachable. New regression analyses
                cannot be run until the service is restored. Existing completed analyses remain accessible.
              </p>
              <p className="mt-2 text-xs text-amber-600">
                Contact support@clarid.ai if this persists.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          title="Total Analyses"
          value={<span className="text-2xl font-bold text-gray-900">{analyses.length}</span>}
        />
        <SummaryCard
          title="Total Findings"
          value={<span className="text-2xl font-bold text-gray-900">{highRiskCount}</span>}
        />
        <SummaryCard
          title="Latest Analysis"
          value={
            latestAnalysis
              ? <span className="text-sm text-gray-700">{new Date(latestAnalysis.createdAt).toLocaleDateString()}</span>
              : <span className="text-gray-400">None</span>
          }
        />
        <SummaryCard
          title="IFLEP Categories"
          value={
            <div className="flex gap-1">
              {['U', 'P', 'S'].map(cat => (
                <span key={cat} className="px-2 py-0.5 bg-ocean-50 text-ocean-700 text-xs font-medium rounded">
                  {cat}
                </span>
              ))}
            </div>
          }
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-ocean-600 text-ocean-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          analyses={analyses}
          onSelect={(id: string) => {
            setSelectedAnalysisId(id);
            setActiveTab('findings');
          }}
        />
      )}

      {activeTab === 'findings' && (
        <FindingsTab
          selectedId={selectedAnalysisId}
          detail={detailData}
          isLoading={detailLoading}
          analyses={analyses}
          onSelect={setSelectedAnalysisId}
        />
      )}

      {activeTab === 'matched-pairs' && (
        <MatchedPairsTab
          selectedId={selectedAnalysisId}
          detail={detailData}
          isLoading={detailLoading}
          analyses={analyses}
          onSelect={setSelectedAnalysisId}
        />
      )}

      {activeTab === 'trends' && (
        <TrendsTab trends={trends} />
      )}
    </div>
  );
}

// ─── Summary Card ───────────────────────────────────────────────────────────

function SummaryCard({ title, value }: { title: string; value: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <p className="text-sm text-gray-500 mb-1">{title}</p>
      <div>{value}</div>
    </div>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({ analyses, onSelect }: { analyses: any[]; onSelect: (id: string) => void }) {
  if (analyses.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow px-6 py-12 text-center">
        <ChartBarSquareIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">
          No regression analyses yet. Run your first analysis to see results here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Analysis History</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IFLEP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {analyses.map((a: any) => (
              <tr
                key={a.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onSelect(a.id)}
              >
                <td className="px-6 py-4 text-sm text-gray-900">
                  {new Date(a.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <AnalysisTypeBadge type={a.analysisType} />
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{a.analysisPeriod}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{a.iflepCategory}</td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {(a.analyzableRecords || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {a.findingsCount}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={a.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Findings Tab ───────────────────────────────────────────────────────────

function FindingsTab({
  selectedId, detail, isLoading, analyses, onSelect,
}: {
  selectedId: string | null;
  detail: any;
  isLoading: boolean;
  analyses: any[];
  onSelect: (id: string) => void;
}) {
  if (!selectedId) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">Select an analysis from the list below to view findings.</p>
        <AnalysisSelector analyses={analyses} onSelect={onSelect} />
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner size="lg" className="py-12" />;

  const findings = detail?.findings || [];

  return (
    <div className="space-y-6">
      {/* Analysis header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {detail?.analysisType} Analysis — {detail?.analysisPeriod}
            </h3>
            <p className="text-sm text-gray-500">
              {detail?.analyzableRecords?.toLocaleString()} records analyzed •{' '}
              {detail?.iflepCategory} • Regulator: {detail?.regulatorConfig}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${import.meta.env.VITE_API_URL || ''}/api/fair-lending/regressions/${selectedId}/export/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ocean-600 border border-ocean-300 rounded-md hover:bg-ocean-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export PDF
            </a>
            <button
              onClick={() => onSelect('')}
              className="text-sm text-ocean-600 hover:text-ocean-700"
            >
              ← Back to list
            </button>
          </div>
        </div>

        {/* AI Narrative */}
        {detail?.narrative && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">AI Analysis Narrative</h4>
            <p className="text-sm text-gray-600 whitespace-pre-line">{detail.narrative}</p>
          </div>
        )}
      </div>

      {/* Findings table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Findings ({findings.length})
          </h2>
        </div>
        {findings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protected Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Odds Ratio / BPS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P-Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IFLEP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {findings.map((f: any) => (
                  <tr key={f.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {f.protectedClassLabel}
                    </td>
                    <td className="px-6 py-4">
                      <RiskLevelBadge level={f.riskLevel} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {f.oddsRatio != null ? (
                        <span>
                          <span className="font-medium">{f.oddsRatio.toFixed(2)}</span>
                          <span className="text-gray-400 text-xs ml-1">
                            [{f.oddsRatioLower?.toFixed(2)}–{f.oddsRatioUpper?.toFixed(2)}]
                          </span>
                        </span>
                      ) : f.basisPoints != null ? (
                        <span>
                          <span className="font-medium">{f.basisPoints.toFixed(1)} bps</span>
                          <span className="text-gray-400 text-xs ml-1">
                            [{f.basisPointsLower?.toFixed(1)}–{f.basisPointsUpper?.toFixed(1)}]
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {f.pValue != null ? f.pValue.toFixed(4) : '—'}
                      {f.isSignificant && (
                        <span className="ml-1 text-red-600 text-xs font-medium">*</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {f.sampleSize?.toLocaleString() ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {f.iflepRiskFactor || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">No findings above the reporting threshold.</p>
          </div>
        )}
      </div>

      {/* Finding interpretations */}
      {findings.filter((f: any) => f.interpretation).length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">AI Interpretations</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {findings.filter((f: any) => f.interpretation).map((f: any) => (
              <div key={f.id} className="px-6 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-sm text-gray-900">{f.protectedClassLabel}</span>
                  <RiskLevelBadge level={f.riskLevel} />
                </div>
                <p className="text-sm text-gray-600">{f.interpretation}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Matched Pairs Tab ──────────────────────────────────────────────────────

function MatchedPairsTab({
  selectedId, detail, isLoading, analyses, onSelect,
}: {
  selectedId: string | null;
  detail: any;
  isLoading: boolean;
  analyses: any[];
  onSelect: (id: string) => void;
}) {
  if (!selectedId) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">Select an analysis to view matched pairs for comparative file review.</p>
        <AnalysisSelector analyses={analyses} onSelect={onSelect} />
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner size="lg" className="py-12" />;

  const pairs = detail?.matchedPairs || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Matched Pairs ({pairs.length})
        </h3>
        <button
          onClick={() => onSelect('')}
          className="text-sm text-ocean-600 hover:text-ocean-700"
        >
          ← Back to list
        </button>
      </div>

      {pairs.length > 0 ? (
        <div className="space-y-4">
          {pairs.map((pair: any, idx: number) => (
            <div key={pair.id || idx} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">
                    Pair #{idx + 1}
                  </span>
                  <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded">
                    {pair.protectedClass}
                  </span>
                  <span className="text-xs text-gray-500">
                    Similarity: {(pair.similarityScore * 100).toFixed(0)}%
                  </span>
                </div>
                <span className="text-xs text-gray-500">{pair.pairType}</span>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <ApplicantCard
                  label="Applicant A (Protected Class)"
                  data={{
                    race: pair.applicantARace,
                    sex: pair.applicantASex,
                    income: pair.applicantAIncome,
                    loanAmt: pair.applicantALoanAmt,
                    loanType: pair.applicantALoanType,
                    action: pair.applicantAAction,
                    rateSpread: pair.applicantARateSpread,
                  }}
                  highlight="red"
                />
                <ApplicantCard
                  label="Applicant B (Reference Group)"
                  data={{
                    race: pair.applicantBRace,
                    sex: pair.applicantBSex,
                    income: pair.applicantBIncome,
                    loanAmt: pair.applicantBLoanAmt,
                    loanType: pair.applicantBLoanType,
                    action: pair.applicantBAction,
                    rateSpread: pair.applicantBRateSpread,
                  }}
                  highlight="green"
                />
              </div>

              {pair.comparisonNarrative && (
                <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                  {pair.comparisonNarrative}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow px-6 py-12 text-center">
          <UserGroupIcon className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">No matched pairs generated for this analysis.</p>
        </div>
      )}
    </div>
  );
}

// ─── Trends Tab ─────────────────────────────────────────────────────────────

function TrendsTab({ trends }: { trends: any[] }) {
  const [filterType, setFilterType] = useState<string>('ALL');

  const filtered = useMemo(() => {
    if (filterType === 'ALL') return trends;
    return trends.filter((t: any) => t.analysisType === filterType);
  }, [trends, filterType]);

  if (trends.length < 2) {
    return (
      <div className="bg-white rounded-lg shadow px-6 py-12 text-center">
        <ArrowTrendingUpIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">
          Need at least two completed analyses to show trends.
        </p>
      </div>
    );
  }

  // Build risk level summary across periods
  const riskPriority: Record<string, number> = { LOW: 1, MODERATE: 2, ELEVATED: 3, HIGH: 4 };
  const riskColors: Record<string, string> = {
    HIGH: '#DC2626', ELEVATED: '#F59E0B', MODERATE: '#3B82F6', LOW: '#10B981',
  };

  // Extract unique protected classes and their max metrics across periods
  const protectedClasses = new Map<string, { periods: Array<{ period: string; oddsRatio?: number; basisPoints?: number; riskLevel: string }> }>();
  for (const t of filtered) {
    for (const f of (t.findings || [])) {
      if (!protectedClasses.has(f.protectedClass)) {
        protectedClasses.set(f.protectedClass, { periods: [] });
      }
      protectedClasses.get(f.protectedClass)!.periods.push({
        period: t.analysisPeriod,
        oddsRatio: f.oddsRatio,
        basisPoints: f.basisPoints,
        riskLevel: f.riskLevel,
      });
    }
  }

  return (
    <div className="space-y-6">
      {/* Filter controls */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">Filter by type:</span>
        {['ALL', 'DENIAL', 'PRICING', 'STEERING'].map(type => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              filterType === type
                ? 'bg-ocean-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {type === 'ALL' ? 'All Types' : type}
          </button>
        ))}
      </div>

      {/* Risk Level Trend Chart (CSS-based) */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Risk Level Trend</h2>
          <p className="text-xs text-gray-500 mt-1">Highest risk level per analysis over time</p>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-end gap-2" style={{ height: 160 }}>
            {filtered.map((t: any) => {
              const highestRisk = (t.findings || []).reduce(
                (max: string, f: any) => (riskPriority[f.riskLevel] || 0) > (riskPriority[max] || 0) ? f.riskLevel : max,
                'LOW',
              );
              const heightPct = ((riskPriority[highestRisk] || 1) / 4) * 100;
              return (
                <div key={t.id} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-medium" style={{ color: riskColors[highestRisk] }}>
                    {highestRisk}
                  </span>
                  <div
                    className="w-full max-w-[48px] rounded-t-md transition-all"
                    style={{
                      height: `${heightPct}%`,
                      backgroundColor: riskColors[highestRisk],
                      opacity: 0.8,
                      minHeight: 8,
                    }}
                  />
                  <span className="text-[10px] text-gray-500 text-center leading-tight mt-1">
                    {t.analysisPeriod}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Protected Class Disparity Tracking */}
      {protectedClasses.size > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Disparity Tracking by Protected Class</h2>
            <p className="text-xs text-gray-500 mt-1">Odds ratio or basis point trends for each group</p>
          </div>
          <div className="px-6 py-4 space-y-6">
            {Array.from(protectedClasses.entries()).map(([className, data]) => {
              const isOddsRatio = data.periods.some(p => p.oddsRatio != null);
              const maxValue = isOddsRatio
                ? Math.max(...data.periods.map(p => p.oddsRatio || 0), 2)
                : Math.max(...data.periods.map(p => Math.abs(p.basisPoints || 0)), 50);

              return (
                <div key={className}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">{className.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-500">{isOddsRatio ? 'Odds Ratio' : 'Basis Points'}</span>
                  </div>
                  <div className="space-y-1.5">
                    {data.periods.map((p, idx) => {
                      const value = isOddsRatio ? (p.oddsRatio || 0) : Math.abs(p.basisPoints || 0);
                      const widthPct = Math.min((value / maxValue) * 100, 100);
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-20 text-right flex-shrink-0">{p.period}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all flex items-center justify-end pr-2"
                              style={{
                                width: `${Math.max(widthPct, 5)}%`,
                                backgroundColor: riskColors[p.riskLevel] || '#9CA3AF',
                              }}
                            >
                              <span className="text-[10px] font-medium text-white">
                                {isOddsRatio ? (p.oddsRatio?.toFixed(2) ?? '') : `${p.basisPoints?.toFixed(0) ?? ''} bps`}
                              </span>
                            </div>
                          </div>
                          <RiskLevelBadge level={p.riskLevel} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Period Detail</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Highest Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((t: any) => {
                const highestRisk = (t.findings || []).reduce(
                  (max: string, f: any) => (riskPriority[f.riskLevel] || 0) > (riskPriority[max] || 0) ? f.riskLevel : max,
                  'LOW',
                );
                return (
                  <tr key={t.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{t.analysisPeriod}</td>
                    <td className="px-6 py-4">
                      <AnalysisTypeBadge type={t.analysisType} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {t.totalRecords?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {t.findings?.length || 0}
                    </td>
                    <td className="px-6 py-4">
                      <RiskLevelBadge level={highestRisk} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────────────────────────

function AnalysisSelector({ analyses, onSelect }: { analyses: any[]; onSelect: (id: string) => void }) {
  const completed = analyses.filter((a: any) => a.status === 'COMPLETED');
  if (completed.length === 0) {
    return <p className="text-sm text-gray-400">No completed analyses available.</p>;
  }

  return (
    <div className="grid gap-3">
      {completed.slice(0, 10).map((a: any) => (
        <button
          key={a.id}
          onClick={() => onSelect(a.id)}
          className="bg-white rounded-lg shadow p-4 text-left hover:ring-2 hover:ring-ocean-300 transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-900">
                {a.analysisType} — {a.analysisPeriod}
              </span>
              <span className="ml-2 text-xs text-gray-500">
                {new Date(a.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{a.findingsCount} findings</span>
              <AnalysisTypeBadge type={a.analysisType} />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function AnalysisTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    DENIAL: 'bg-red-50 text-red-700',
    PRICING: 'bg-amber-50 text-amber-700',
    STEERING: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[type] || 'bg-gray-50 text-gray-700'}`}>
      {type}
    </span>
  );
}

function RiskLevelBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    HIGH: 'bg-red-100 text-red-800',
    ELEVATED: 'bg-orange-100 text-orange-800',
    MODERATE: 'bg-amber-100 text-amber-800',
    LOW: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[level] || 'bg-gray-100 text-gray-700'}`}>
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    COMPLETED: 'bg-green-100 text-green-800',
    RUNNING: 'bg-blue-100 text-blue-800',
    QUEUED: 'bg-amber-100 text-amber-800',
    PENDING: 'bg-gray-100 text-gray-700',
    FAILED: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function ApplicantCard({
  label,
  data,
  highlight,
}: {
  label: string;
  data: {
    race: string;
    sex: string;
    income: number;
    loanAmt: number;
    loanType: string;
    action: string;
    rateSpread?: number;
  };
  highlight: 'red' | 'green';
}) {
  const borderColor = highlight === 'red' ? 'border-red-200' : 'border-green-200';
  return (
    <div className={`border ${borderColor} rounded-lg p-4`}>
      <p className="text-xs font-medium text-gray-500 mb-3">{label}</p>
      <div className="space-y-1 text-sm">
        <Row label="Race/Ethnicity" value={data.race?.replace(/_/g, ' ')} />
        <Row label="Sex" value={data.sex?.replace(/_/g, ' ')} />
        <Row label="Income" value={`$${(data.income || 0).toLocaleString()}K`} />
        <Row label="Loan Amount" value={`$${(data.loanAmt || 0).toLocaleString()}K`} />
        <Row label="Loan Type" value={data.loanType?.replace(/_/g, ' ')} />
        <Row label="Action" value={data.action} />
        {data.rateSpread != null && (
          <Row label="Rate Spread" value={`${data.rateSpread.toFixed(3)}%`} />
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value || '—'}</span>
    </div>
  );
}
